import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PaperPlaneTilt, ChatCircle, Plus, X, UsersThree, ChatText, Check, Trash, ArrowBendUpLeft, Paperclip, Flag, Smiley, ImageSquare, Microphone, Phone, CaretDown, CalendarBlank } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationContext";
import { useRTC } from "@/context/RTCContext";
import { useCall } from "@/context/CallContext";
import MessageBubble from "@/components/MessageBubble";
import MessageInfoModal from "@/components/MessageInfoModal";
import ForwardModal from "@/components/ForwardModal";
import VoiceRecorder from "@/components/VoiceRecorder";
import { useNavigate, useLocation, NavLink } from "react-router-dom";
import TeamThreadList from "@/components/message/TeamThreadList";
import "./dashboard-premium.css";
import TeamChatHeader from "@/components/message/TeamChatHeader";
import MessagesTabStrip from "@/components/message/MessagesTabStrip";
import { FilesView, PhotosView } from "@/components/message/TeamChatMediaViews";
import EmptyState from "@/components/EmptyState";
import { sanitizeHtmlToMessage, sanitizePlainTextToMessage } from "@/lib/pasteSanitize";
import { shouldSendOnEnter } from "@/lib/composerKeys";
import TeamChatEditor from "@/components/message/TeamChatEditor";

export default function Messages() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { unreadTotal: teamUnread, smsUnread } = useNotifications();
  // Team Chat is a multi-user collaboration feature — only meaningful for business orgs.
  const isIndividualOrg = user?.organization?.type === "individual";
  const teamChatEnabled = !isIndividualOrg;
  // Admin without their own seat can use team chat freely, but cannot send
  // customer SMS. Default them to the team tab so the landing view is useful.
  const adminSmsLocked = user?.role === "admin" && !user?.has_own_seat;
  const initialTab = (() => {
    const q = new URLSearchParams(location.search).get("tab");
    if (q === "team" && teamChatEnabled) return "team";
    if (q === "sms") return "sms";
    return (adminSmsLocked && teamChatEnabled) ? "team" : "sms";
  })();
  const [tab, setTab] = useState(initialTab);

  // React to ?tab= changes (e.g. clicking a notification toast)
  useEffect(() => {
    const t = new URLSearchParams(location.search).get("tab");
    if (t === "team" && teamChatEnabled) setTab("team");
    else if (t === "sms") setTab("sms");
  }, [location.search, teamChatEnabled]);

  const changeTab = (next) => {
    if (next === "team" && !teamChatEnabled) return;
    setTab(next);
    navigate(`/app/sms?tab=${next}`, { replace: true });
  };

  return (
    <div className="hbf-premium-scope h-full flex flex-col px-8 pt-5 pb-4 overflow-hidden" data-testid="messages-page">
      {/* Compact header — title only (tabs moved into the panel sidebar) */}
      <div className="flex items-center justify-between gap-6 shrink-0" data-testid="messages-header">
        <div className="flex items-baseline gap-3 min-w-0">
          <h1 className="hv-title" style={{ fontSize: 30 }}><strong>Messages</strong></h1>
          <span className="hidden md:inline text-[12px] text-[var(--text-muted)] truncate">
            {teamChatEnabled
              ? "SMS to customers + free internal chat & groups for your team"
              : "Send SMS to your customers"}
          </span>
        </div>
      </div>

      {!teamChatEnabled && (
        <NavLink
          to="/app/settings"
          data-testid="messages-upgrade-banner"
          className="cta-shimmer mt-3 shrink-0 relative overflow-hidden rounded-lg shadow-md shadow-[#5B5FC7]/15 hover:shadow-lg hover:shadow-[#5B5FC7]/25 transition-all duration-300 hover:-translate-y-0.5"
          style={{
            background: "linear-gradient(120deg, #EEF0FF 0%, #E0E1FA 60%, #F5E8FF 100%)",
            border: "1px solid rgba(91,95,199,0.25)",
          }}
        >
          <div className="relative px-5 py-4 flex items-center gap-5">
            {/* Left badge */}
            <div className="w-11 h-11 rounded-xl shrink-0 flex items-center justify-center shadow-md shadow-[#5B5FC7]/30"
                 style={{ background: "linear-gradient(135deg, #5B5FC7 0%, #A78BFA 100%)" }}>
              <UsersThree size={20} weight="fill" className="text-white" />
            </div>

            {/* Copy */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-display font-extrabold text-[15px] text-[#1F2452]">
                  Your workspace is in solo mode
                </span>
                <span className="text-[10px] font-mono tracking-widest font-bold uppercase bg-white/70 text-[#5B5FC7] border border-[#5B5FC7]/30 px-1.5 py-0.5 rounded">
                  Business unlock
                </span>
              </div>
              <div className="mt-1 flex items-center gap-3 flex-wrap text-[12.5px] text-[#3A3FA8]">
                <span className="flex items-center gap-1.5">
                  <ChatText size={13} weight="fill" className="text-[#5B5FC7]" /> Team Chat
                </span>
                <span className="opacity-30">•</span>
                <span className="flex items-center gap-1.5">
                  <CalendarBlank size={13} weight="fill" className="text-[#5B5FC7]" /> Meetings
                </span>
                <span className="opacity-30">•</span>
                <span className="flex items-center gap-1.5">
                  <Phone size={13} weight="fill" className="text-[#5B5FC7]" /> Group Calls
                </span>
                <span className="opacity-30">•</span>
                <span className="flex items-center gap-1.5">
                  <UsersThree size={13} weight="fill" className="text-[#5B5FC7]" /> Unlimited Teammates
                </span>
              </div>
            </div>

            {/* CTA pill */}
            <div className="shrink-0">
              <span
                className="inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-widest text-white px-4 py-2.5 rounded-md shadow-md shadow-[#5B5FC7]/30 group-hover:shadow-lg transition-shadow"
                style={{ background: "linear-gradient(135deg, #5B5FC7 0%, #7A7FE0 100%)" }}
              >
                Upgrade <span className="cta-arrow">→</span>
              </span>
              <div className="text-[10px] font-mono text-[#5B5FC7]/80 text-center mt-1.5">~30s · KYC-instant</div>
            </div>
          </div>
        </NavLink>
      )}

      <div className="flex-1 min-h-0 mt-4 hv-shell hd-fade-up hd-stagger-1">
        {tab === "sms" && adminSmsLocked
          ? <CustomerSmsAdminLocked />
          : tab === "sms" || !teamChatEnabled
            ? <SmsPanel tab={tab} teamChatEnabled={teamChatEnabled} smsUnread={smsUnread} teamUnread={teamUnread} onChangeTab={changeTab} />
            : <TeamChatPanel tab={tab} smsUnread={smsUnread} teamUnread={teamUnread} onChangeTab={changeTab} />}
      </div>
    </div>
  );
}

/**
 * Small in-panel lock shown when an admin without their own Communication
 * Seat switches to the Customer SMS tab. Team Chat continues to work — this
 * only guards the customer-facing SMS pipeline (which needs a business
 * number to send from).
 */
function CustomerSmsAdminLocked() {
  const navigate = useNavigate();
  return (
    <div className="flex-1 min-h-0 flex items-center justify-center px-8" data-testid="sms-admin-locked">
      <div className="max-w-md text-center">
        <div className="font-mono text-[11px] tracking-widest text-[var(--text-muted)] uppercase mb-3">
          Locked · Customer SMS
        </div>
        <h2 className="font-display font-black text-[24px] leading-[1.1] mb-3">
          Grab a seat before you can text customers.
        </h2>
        <p className="text-[13px] text-[var(--text-secondary)] leading-relaxed mb-6">
          Customer SMS sends from your personal Communication Seat's business number. Your team chat + groups remain fully open regardless.
        </p>
        <button
          className="btn-primary inline-flex items-center gap-2"
          onClick={() => navigate("/app/numbers?admin_seat=1")}
          data-testid="sms-admin-locked-cta"
        >
          Get a seat for yourself →
        </button>
      </div>
    </div>
  );
}

// =============== Customer SMS ===============
function SmsPanel({ tab, teamChatEnabled, smsUnread, teamUnread, onChangeTab }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshUnread } = useNotifications();
  const { startCall } = useCall();
  const [contacts, setContacts] = useState([]);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState([]);
  const [body, setBody] = useState("");
  const [quickOpen, setQuickOpen] = useState(false);
  // contact_id -> unread inbound count (excluding the currently-open thread, which auto-marks as read)
  const [unreadByContact, setUnreadByContact] = useState({});
  // Inbound conversations from numbers that don't match any contact (rendered above contacts).
  const [orphanThreads, setOrphanThreads] = useState([]);
  // Orphan thread currently being "saved as contact" via the modal (or null).
  const [saveOrphan, setSaveOrphan] = useState(null);

  const loadUnreadMap = useCallback(async () => {
    try {
      const { data } = await api.get("/sms/unread-by-contact");
      setUnreadByContact(data.counts || {});
      setOrphanThreads(data.orphan_threads || []);
    } catch (err) { /* silent — polling */ }
  }, []);
  const smsScrollRef = useRef(null);
  const [smsShowJump, setSmsShowJump] = useState(false);
  const [smsNewCount, setSmsNewCount] = useState(0);
  const smsLastIdsRef = useRef(new Set());
  const pendingMsgIdRef = useRef(null);

  const isNearBottom = (el, threshold = 24) =>
    !el || (el.scrollHeight - el.scrollTop - el.clientHeight) < threshold;

  const onSmsScroll = () => {
    const near = isNearBottom(smsScrollRef.current);
    setSmsShowJump(!near);
    if (near) setSmsNewCount(0);
  };

  const jumpToLatestSms = () => {
    const el = smsScrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    setSmsNewCount(0);
  };

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/contacts");
      setContacts(data);
      if (data[0]) setActive(data[0]);
    })();
    loadUnreadMap();
  }, [loadUnreadMap]);

  // Re-poll unread map alongside the global SMS unread badge (every 6s)
  useEffect(() => { loadUnreadMap(); }, [smsUnread, loadUnreadMap]);

  // Respond to deep-link query params (?contact=...&msg=...)
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const cid = q.get("contact");
    const mid = q.get("msg");
    if (!cid && !mid) return;
    if (mid) pendingMsgIdRef.current = mid;
    if (cid && contacts.length > 0) {
      const c = contacts.find((x) => x.id === cid);
      if (c && (!active || active.id !== c.id)) setActive(c);
      // Strip params so a re-render doesn't re-trigger
      navigate("/app/sms?tab=sms", { replace: true });
    }
  }, [location.search, contacts, active, navigate]);

  useEffect(() => {
    if (!active) return;
    if (active.ad_hoc) { setThread([]); return; }
    (async () => {
      // Orphan thread (unknown sender) — fetch by from_number; otherwise by contact_id.
      const url = active.from_number_only
        ? `/sms?from_number=${encodeURIComponent(active.phone)}`
        : `/sms?contact_id=${active.id}`;
      const { data } = await api.get(url);
      const serverRows = data.reverse();
      // Merge server rows with any in-flight / failed optimistic rows so a
      // refetch that races the POST response never drops a sent message.
      setThread((prev) => {
        const serverIds = new Set(serverRows.map((m) => m.id));
        const pending = prev.filter(
          (m) => m._optimistic || m.status === "failed" || (!serverIds.has(m.id) && (m.status === "queued" || m.status === "sending")),
        );
        // Non-blocking Sentry breadcrumb: if a queued optimistic row (with a
        // real twilio_sid attached — i.e. server already ACKed the send)
        // is missing from the server response, that's a scope-drift bug.
        // Telemetry failures MUST NOT interrupt UI.
        try {
          const confirmed = prev.filter((m) => !m._optimistic && m.twilio_sid && !serverIds.has(m.id));
          if (confirmed.length > 0) {
            // Lazy-import so a Sentry SDK failure never breaks the pipeline.
            import("@sentry/react")
              .then((Sentry) => {
                try {
                  Sentry.captureMessage("sms.optimistic.confirmed_row_missing_from_refetch", {
                    level: "warning",
                    extra: { count: confirmed.length, ids: confirmed.map((m) => m.id) },
                  });
                } catch (_e) { /* swallow */ }
              })
              .catch(() => { /* swallow */ });
          }
        } catch (_e) {
          /* swallow — telemetry must never break the pipeline */
        }
        return [...serverRows, ...pending];
      });
      // Mark inbound SMS in this thread as read so the badge decrements
      try {
        const payload = active.from_number_only
          ? { from_number: active.phone }
          : { contact_id: active.id };
        await api.post("/sms/mark-thread-read", payload);
        // Optimistically clear this contact's per-row badge, then refresh truth
        if (active.from_number_only) {
          setOrphanThreads(arr => arr.map(o => o.from_number === active.phone ? { ...o, unread: 0 } : o));
        } else {
          setUnreadByContact(m => { const n = { ...m }; delete n[active.id]; return n; });
        }
        loadUnreadMap();
        // Refresh global SMS unread so badges update immediately
        refreshUnread?.();
      } catch (err) {
        console.warn("SMS mark-thread-read failed (non-fatal):", err?.message || err);
      }
    })();
  }, [active, refreshUnread, loadUnreadMap]);

  // Snap to bottom when switching threads or new messages arrive (only if already near bottom)
  // Also track new inbound messages while user is scrolled up.
  // Also handle pending deep-link msg id (scroll into view + flash).
  useEffect(() => {
    const el = smsScrollRef.current;
    if (!el) return;
    const prevIds = smsLastIdsRef.current;
    const currentIds = new Set(thread.map(m => m.id));
    const inboundNew = thread.filter(m => m.direction === "inbound" && !prevIds.has(m.id) && prevIds.size > 0);
    if (isNearBottom(el, 200)) {
      requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
      setSmsNewCount(0);
    } else if (inboundNew.length > 0) {
      setSmsNewCount(c => c + inboundNew.length);
    }
    smsLastIdsRef.current = currentIds;

    // Deep-link: if there's a pending msg id, scroll & flash it
    const pending = pendingMsgIdRef.current;
    if (pending && thread.some(m => m.id === pending)) {
      requestAnimationFrame(() => {
        const node = document.querySelector(`[data-testid="msg-${CSS.escape(pending)}"]`);
        if (node) {
          node.scrollIntoView({ behavior: "smooth", block: "center" });
          node.classList.add("msg-flash");
          setTimeout(() => node.classList.remove("msg-flash"), 2500);
        }
        pendingMsgIdRef.current = null;
      });
    }
  }, [thread]);

  // Reset counter + ids on thread switch
  useEffect(() => {
    smsLastIdsRef.current = new Set();
    setSmsNewCount(0);
    setSmsShowJump(false);
  }, [active?.id]);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim() || !active) return;
    const payload = { to_number: active.phone, body };
    if (active.id && !active.ad_hoc && !active.from_number_only) payload.contact_id = active.id;
    // Optimistic append — reserve a temp id so a refetch race can't drop it.
    const tempId = `optimistic-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const bodySent = body;
    const optimistic = {
      id: tempId,
      _optimistic: true,
      direction: "outbound",
      body: bodySent,
      to_number: active.phone,
      from_number: null,
      contact_id: payload.contact_id || null,
      status: "queued",
      created_at: new Date().toISOString(),
    };
    setThread((t) => [...t, optimistic]);
    setBody("");
    try {
      const { data } = await api.post("/sms", payload);
      // Reconcile — replace the optimistic row with the server row (keep at same position).
      setThread((t) => t.map((m) => (m.id === tempId ? { ...data, _optimistic: false } : m)));
      toast.success("Message sent");
    } catch (err) {
      // Mark the optimistic row as failed so it stays visible with a retry hint.
      setThread((t) => t.map((m) => (m.id === tempId ? { ...m, status: "failed", _error: err?.response?.data?.detail || "Failed to send" } : m)));
      if (err?.response?.status === 429) {
        toast.error(err.response.data?.detail || "SMS rate limit reached. Please retry shortly.", {
          duration: 6000,
        });
      } else {
        toast.error(err?.response?.data?.detail || "Failed to send SMS");
      }
    }
  };

  return (
    <div className="hd-card hd-card--static grid grid-cols-12 h-full" data-testid="sms-panel">
      <div className="col-span-4 xl:col-span-3 border-r border-[var(--border)] overflow-y-auto min-h-0">
        {teamChatEnabled && (
          <MessagesTabStrip tab={tab} smsUnread={smsUnread} teamUnread={teamUnread} onChange={onChangeTab} />
        )}
        <div className="px-3 py-2 border-b border-[var(--border)] bg-[var(--bg-sidebar)]">
          <button onClick={() => setQuickOpen(true)} data-testid="sms-quick-btn"
                  className="hd-btn hd-btn--primary w-full justify-center !text-[12px]">
            <Plus size={14} weight="bold" /> New message (no contact needed)
          </button>
        </div>
        {active?.ad_hoc && (
          <button onClick={() => setActive(active)}
                  className="w-full text-left px-4 py-3 border-b border-[var(--border)] bg-[var(--bg-sidebar)] flex items-center gap-3"
                  data-testid="sms-adhoc-row">
            <div className="w-9 h-9 rounded-xl border border-[var(--border)] bg-white flex items-center justify-center font-mono text-[11px]">?</div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[14px] truncate">Quick message</div>
              <div className="font-mono text-[11px] text-[var(--text-muted)] truncate">{active.phone}</div>
            </div>
          </button>
        )}

        {/* Orphan inbound threads — senders not in your contacts */}
        {orphanThreads.length > 0 && (
          <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-widest text-[var(--text-muted)] font-semibold border-b border-[var(--border)] bg-[var(--bg-sidebar)]">
            Unknown senders
          </div>
        )}
        {orphanThreads.map((o) => {
          const isActiveRow = active?.from_number_only && active?.phone === o.from_number;
          const hasUnread = o.unread > 0 && !isActiveRow;
          const preview = o.last_message?.body || "";
          const openRow = () => setActive({ phone: o.from_number, name: "Unknown sender", from_number_only: true, id: `orphan:${o.from_number}` });
          return (
            <div
              key={`orphan-${o.from_number}`}
              onClick={openRow}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openRow(); } }}
              data-testid={`sms-orphan-row-${o.from_number}`}
              className={`relative w-full text-left px-4 py-3 border-b border-[var(--border)] hover:bg-[var(--bg-sidebar)] flex items-center gap-3 cursor-pointer group ${isActiveRow ? "bg-[var(--bg-sidebar)]" : ""} ${hasUnread ? "bg-[#EEF0FF]/40" : ""}`}
            >
              {hasUnread && <span className="absolute left-0 top-0 bottom-0 w-1 bg-[var(--accent)]" />}
              <div className="relative shrink-0">
                <div className="w-9 h-9 rounded-xl border border-[var(--border)] bg-white flex items-center justify-center font-mono text-[11px] text-[var(--text-muted)]">?</div>
                {hasUnread && (
                  <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-[var(--accent)] border-2 border-white" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div className={`truncate text-[14px] ${hasUnread ? "font-extrabold text-[var(--text-primary)]" : "font-semibold"}`}>
                    {o.from_number}
                  </div>
                  {hasUnread && (
                    <span className="bg-[var(--accent)] text-white text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0"
                          data-testid={`sms-orphan-unread-${o.from_number}`}>
                      {o.unread > 99 ? "99+" : o.unread}
                    </span>
                  )}
                </div>
                <div className={`text-[11px] truncate ${hasUnread ? "text-[var(--text-secondary)] font-medium" : "text-[var(--text-muted)]"}`}>
                  {preview || <span className="uppercase tracking-wider">No preview</span>}
                </div>
              </div>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setSaveOrphan(o); }}
                title="Save as contact"
                data-testid={`sms-orphan-save-${o.from_number}`}
                className="shrink-0 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity text-[11px] font-semibold border border-[var(--border)] bg-white hover:bg-[var(--accent)] hover:text-white hover:border-[var(--accent)] px-2 py-1 rounded"
              >
                + Save
              </button>
            </div>
          );
        })}

        {contacts.map((c) => {
          const unread = unreadByContact[c.id] || 0;
          const isActiveRow = active?.id === c.id && !active.ad_hoc;
          const hasUnread = unread > 0 && !isActiveRow;
          return (
            <button key={c.id} onClick={() => setActive(c)} data-testid={`thread-${c.id}`}
                    className={`relative w-full text-left px-4 py-3 border-b border-[var(--border)] hover:bg-[var(--bg-sidebar)] flex items-center gap-3 ${isActiveRow ? "bg-[var(--bg-sidebar)]" : ""} ${hasUnread ? "bg-[#EEF0FF]/40" : ""}`}>
              {hasUnread && <span className="absolute left-0 top-0 bottom-0 w-1 bg-[var(--accent)]" data-testid={`sms-row-unread-stripe-${c.id}`} />}
              <div className="relative shrink-0">
                <img src={c.avatar_url} className="w-9 h-9 rounded-xl border border-[var(--border)]" alt="" />
                {hasUnread && (
                  <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-[var(--accent)] border-2 border-white" data-testid={`sms-row-dot-${c.id}`} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div className={`truncate text-[14px] ${hasUnread ? "font-extrabold text-[var(--text-primary)]" : "font-semibold"}`}>{c.name}</div>
                  {hasUnread && (
                    <span className="bg-[var(--accent)] text-white text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0"
                          data-testid={`sms-row-unread-${c.id}`}>
                      {unread > 99 ? "99+" : unread}
                    </span>
                  )}
                </div>
                <div className={`font-mono text-[11px] truncate ${hasUnread ? "text-[var(--text-secondary)]" : "text-[var(--text-muted)]"}`}>{c.phone}</div>
              </div>
            </button>
          );
        })}

        {contacts.length === 0 && orphanThreads.length === 0 && !active && (
          <div className="px-2 py-6">
            <EmptyState
              icon="sms"
              title="No conversations yet"
              description="Send your first SMS from a contact, or start an ad-hoc message — replies from unknown numbers appear here automatically."
              primaryAction={{ label: "Send a quick message", onClick: () => setQuickOpen(true) }}
              secondaryAction={{ label: "Add a contact", onClick: () => (window.location.href = "/app/contacts") }}
              compact
              testId="empty-state-sms"
            />
          </div>
        )}
      </div>

      <div className="col-span-8 xl:col-span-9 flex flex-col min-h-0">
        {active ? (
          <>
            <div className="px-5 py-3 border-b border-[var(--border)] flex items-center gap-3 shrink-0">
              {active.avatar_url
                ? <img src={active.avatar_url} className="w-8 h-8 border border-[var(--border)]" alt="" />
                : <div className="w-8 h-8 border border-[var(--border)] bg-[var(--bg-sidebar)] flex items-center justify-center font-mono text-[10px]">?</div>}
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[14px] truncate">{active.name || "Quick message"}</div>
                <div className="font-mono text-[11px] text-[var(--text-muted)]">{active.phone}</div>
              </div>
              <button
                type="button"
                onClick={() => startCall({ to_number: active.phone, to_name: active.name || active.phone })}
                disabled={!active.phone}
                title={`Call ${active.name || active.phone}`}
                data-testid="sms-header-call-btn"
                className="flex items-center gap-1.5 bg-[#10B981] hover:bg-[#059669] disabled:opacity-50 disabled:cursor-not-allowed text-white text-[12px] font-semibold px-3 py-2 rounded transition-colors"
              >
                <Phone size={14} weight="fill" /> Call
              </button>
            </div>
            <div className="flex-1 min-h-0 relative">
              <div ref={smsScrollRef} onScroll={onSmsScroll}
                   className="absolute inset-0 overflow-y-auto p-5 space-y-3 bg-[var(--bg)]"
                   data-testid="sms-thread-scroll">
                {thread.map((m) => (
                  <div key={m.id} className={`flex ${m.direction === "outbound" ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                    <div className={`max-w-[70%] px-4 py-2.5 text-[14px] ${m.direction === "outbound" ? "hd-bubble-out" : "hd-bubble-in"} ${m.status === "failed" ? "opacity-70 ring-1 ring-red-400" : ""}`}>
                      <div>{m.body}</div>
                      <div className={`font-mono text-[10px] mt-1 flex items-center gap-1.5 ${m.direction === "outbound" ? "text-white/70" : "text-[var(--text-muted)]"}`}>
                        <span>{new Date(m.created_at).toLocaleString()}</span>
                        {m.direction === "outbound" && m.status === "queued" && <span data-testid={`msg-status-${m.id}`}>· sending</span>}
                        {m.direction === "outbound" && m.status === "sent" && <span data-testid={`msg-status-${m.id}`}>· sent</span>}
                        {m.direction === "outbound" && m.status === "delivered" && <span data-testid={`msg-status-${m.id}`}>· delivered</span>}
                        {m.direction === "outbound" && (m.status === "failed" || m.status === "undelivered") && (
                          <span className="text-red-200" data-testid={`msg-status-${m.id}`}>· failed{m._error ? `: ${m._error}` : ""}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {thread.length === 0 && <div className="text-center text-[var(--text-muted)] py-10">No messages yet — say hi</div>}
              </div>
              {smsShowJump && (
                <button type="button" onClick={jumpToLatestSms}
                        className="absolute bottom-4 right-4 z-10 bg-[var(--accent)] text-white rounded-full min-w-9 h-9 px-2.5 flex items-center justify-center gap-1 shadow-lg hover:opacity-90 transition-opacity"
                        title="Jump to latest" data-testid="sms-jump-to-latest">
                  <CaretDown size={16} weight="bold" />
                  {smsNewCount > 0 && (
                    <span className="text-[11px] font-bold leading-none" data-testid="sms-jump-badge">
                      {smsNewCount > 99 ? "99+" : smsNewCount}
                    </span>
                  )}
                </button>
              )}
            </div>
            <form onSubmit={send} className="border-t border-[var(--border)] p-3 flex gap-2 bg-white shrink-0">
              <input value={body} onChange={(e) => setBody(e.target.value)} placeholder="Type a message..." className="input" data-testid="sms-input" />
              <button type="submit" className="btn-primary flex items-center gap-2" data-testid="sms-send">
                <PaperPlaneTilt size={14} weight="fill" /> Send
              </button>
            </form>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-[var(--text-muted)]">
            <ChatCircle size={48} />
            <div className="mt-3">Select a conversation</div>
          </div>
        )}
      </div>

      <QuickSmsModal open={quickOpen} onClose={() => setQuickOpen(false)}
        onCreated={(phone) => { setActive({ phone, ad_hoc: true, name: "Quick message" }); setQuickOpen(false); }} />

      <SaveOrphanModal
        orphan={saveOrphan}
        onClose={() => setSaveOrphan(null)}
        onSaved={async (newContact) => {
          setSaveOrphan(null);
          // Reload contacts so the new one appears, and refresh orphan map (the saved orphan should disappear).
          const { data } = await api.get("/contacts");
          setContacts(data);
          loadUnreadMap();
          // Focus the new contact's thread so the user sees the messages got adopted.
          const target = data.find(c => c.id === newContact.id) || newContact;
          setActive(target);
        }}
      />
    </div>
  );
}

function SaveOrphanModal({ orphan, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", company: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { if (orphan) { setForm({ name: "", company: "", email: "" }); setErr(""); } }, [orphan]);
  if (!orphan) return null;
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setErr("Name is required."); return; }
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/contacts", {
        name: form.name.trim(),
        phone: orphan.from_number,
        email: form.email.trim() || null,
        company: form.company.trim() || null,
        tags: ["from-sms"],
        notes: `Saved from SMS thread (${orphan.unread || 0} adopted message${(orphan.unread || 0) === 1 ? "" : "s"}).`,
      });
      onSaved?.(data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="hd-modal-scrim" data-testid="save-orphan-modal">
      <div className="hd-modal w-full max-w-md">
        <div className="px-5 py-4 border-b border-[var(--border)] flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-widest text-[var(--text-muted)] font-semibold">UNKNOWN SENDER → CONTACT</div>
            <div className="font-display font-bold text-[18px]">Save as contact</div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-[var(--bg-sidebar)]" data-testid="save-orphan-close"><X size={16}/></button>
        </div>
        <form onSubmit={submit} className="px-5 py-5 space-y-4">
          <div className="border border-[var(--border)] bg-[var(--bg-sidebar)] px-3 py-2 flex items-center justify-between">
            <div>
              <div className="text-[10px] font-mono tracking-widest text-[var(--text-muted)]">PHONE</div>
              <div className="font-mono text-[14px] font-semibold">{orphan.from_number}</div>
            </div>
            <div className="text-[11px] text-[var(--text-muted)]">
              {orphan.unread || 0} message{(orphan.unread || 0) === 1 ? "" : "s"} will be linked
            </div>
          </div>
          <div>
            <label className="label">Name <span className="text-[var(--danger)]">*</span></label>
            <input autoFocus required className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="save-orphan-name" placeholder="e.g. Aiko Tanaka" />
          </div>
          <div>
            <label className="label">Company <span className="text-[var(--text-muted)] font-normal">(optional)</span></label>
            <input className="input" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="save-orphan-company" />
          </div>
          <div>
            <label className="label">Email <span className="text-[var(--text-muted)] font-normal">(optional)</span></label>
            <input type="email" className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="save-orphan-email" />
          </div>
          {err && <div className="text-[13px] text-[var(--danger)]" data-testid="save-orphan-error">{err}</div>}
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} disabled={busy} className="btn-ghost flex-1">Cancel</button>
            <button type="submit" disabled={busy || !form.name.trim()} className="btn-primary flex-1" data-testid="save-orphan-submit">
              {busy ? "Saving…" : "Save contact"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function QuickSmsModal({ open, onClose, onCreated }) {
  const [phone, setPhone] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { if (!open) { setPhone(""); setBody(""); setErr(""); } }, [open]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  const submit = async (e) => {
    e.preventDefault();
    if (!phone.trim() || !body.trim()) return;
    setBusy(true); setErr("");
    try {
      const num = phone.trim().startsWith("+") ? phone.trim() : `+${phone.trim().replace(/\D/g, "")}`;
      await api.post("/sms", { to_number: num, body: body.trim() });
      onCreated?.(num);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="hd-modal-scrim" data-testid="quick-sms-modal">
      <div className="hd-modal w-full max-w-md">
        <div className="px-5 py-4 border-b border-[var(--border)] flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-widest text-[var(--text-muted)] font-semibold">QUICK SMS</div>
            <div className="font-display font-bold text-[18px]">Text any number</div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-[var(--bg-sidebar)]" data-testid="quick-sms-close"><X size={16}/></button>
        </div>
        <form onSubmit={submit} className="px-5 py-5">
          <label className="block text-[11px] tracking-widest font-semibold text-[var(--text-muted)] mb-1">TO (E.164)</label>
          <input autoFocus value={phone} onChange={(e)=>setPhone(e.target.value)} placeholder="+15551234567" className="input font-mono" data-testid="quick-sms-to" />
          <label className="block text-[11px] tracking-widest font-semibold text-[var(--text-muted)] mb-1 mt-4">MESSAGE</label>
          <textarea value={body} onChange={(e)=>setBody(e.target.value)} rows={4} maxLength={1000} placeholder="Hi! Following up on..." className="input" data-testid="quick-sms-body" />
          <div className="text-[11px] text-[var(--text-muted)] mt-1 text-right">{body.length}/1000</div>
          {err && <div className="text-[12px] text-[var(--danger)] mt-2" data-testid="quick-sms-error">{err}</div>}
          <button type="submit" disabled={busy || !phone || !body} className="mt-3 w-full bg-[var(--accent)] hover:opacity-90 disabled:opacity-50 text-white font-semibold py-2.5 flex items-center justify-center gap-2" data-testid="quick-sms-send">
            <PaperPlaneTilt size={14} weight="fill" /> {busy ? "Sending..." : "Send SMS"}
          </button>
        </form>
      </div>
    </div>
  );
}

// =============== Internal Team Chat + Groups ===============
function TeamChatPanel({ tab, smsUnread, teamUnread, onChangeTab }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refreshUnread } = useNotifications();
  const { joinRoom } = useRTC();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  // iter161-tabs — active view for the Team-Chat right pane.
  // "chat" = existing message list + composer (default).
  // "files" = list of every non-image attachment shared in this thread.
  // "photos" = grid of every image attachment shared in this thread.
  // Reset to "chat" whenever the active thread changes so switching
  // conversations doesn't leave the user staring at the wrong tab.
  const [teamChatTab, setTeamChatTab] = useState("chat");

  // Reset the team-chat tab to "chat" whenever the active thread
  // changes, so switching conversations doesn't leave the user on the
  // Files or Photos tab of the previous conversation.
  useEffect(() => { setTeamChatTab("chat"); }, [active?.id]);
  const [groupModal, setGroupModal] = useState(null);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [mutedThreads, setMutedThreads] = useState(new Set());
  const [chatSettings, setChatSettings] = useState({ delete_window_seconds: 300, edit_window_seconds: 300 });
  const [replyTo, setReplyTo] = useState(null);
  const [infoMsgId, setInfoMsgId] = useState(null);
  const [forwardMsg, setForwardMsg] = useState(null);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [priority, setPriority] = useState(null); // 'low' | 'medium' | 'high' | null
  const [blockedUsers, setBlockedUsers] = useState(new Set());
  const [moreOpen, setMoreOpen] = useState(false);
  const [recordingVoice, setRecordingVoice] = useState(false);
  // iter135 P1 · emoji picker for compose. Previous onClick just focused
  // the input, so it did nothing. Now clicking opens a small popup that
  // inserts the picked emoji at the cursor position.
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const insertEmoji = (emoji) => {
    const el = inputRef.current;
    if (!el) { setBody((b) => b + emoji); setEmojiPickerOpen(false); return; }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    const next = body.slice(0, start) + emoji + body.slice(end);
    setBody(next);
    setEmojiPickerOpen(false);
    requestAnimationFrame(() => {
      try {
        el.focus();
        const pos = start + emoji.length;
        el.setSelectionRange(pos, pos);
      } catch (_e) { /* focus race, non-fatal */ }
    });
  };
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const [members, setMembers] = useState([]); // for @mention autocomplete
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const inputRef = useRef(null);
  const editorRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(0);
  const scrollRef = useRef(null);
  const [teamShowJump, setTeamShowJump] = useState(false);
  const [teamNewCount, setTeamNewCount] = useState(0);
  const teamLastIdsRef = useRef(new Set());
  const pendingTeamMsgIdRef = useRef(null);

  const isNearBottom = (el, threshold = 24) =>
    !el || (el.scrollHeight - el.scrollTop - el.clientHeight) < threshold;

  const onTeamScroll = () => {
    const near = isNearBottom(scrollRef.current);
    setTeamShowJump(!near);
    if (near) setTeamNewCount(0);
  };

  const jumpToLatestTeam = () => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    setTeamNewCount(0);
  };

  const loadThreads = useCallback(async () => {
    try {
      const { data } = await api.get("/team-chat/threads");
      setThreads(data);
      if (!active && data.length) setActive(data[0]);
    } catch (e) { console.error(e); }
  }, [active]);

  // Track the "active" thread request so a slow response from a previously
  // selected thread never overwrites the current thread's messages (the
  // "stale conversation displayed after switching chats" bug).
  const activeLoadTokenRef = useRef(0);
  const loadMessages = useCallback(async (a) => {
    if (!a) return;
    const token = ++activeLoadTokenRef.current;
    const q = a.kind === "group" ? `group_id=${a.id}` : `with_user=${a.id}`;
    const { data } = await api.get(`/team-chat/messages?${q}`);
    // Bail out if a newer selection has superseded this one.
    if (token !== activeLoadTokenRef.current) return;
    const el = scrollRef.current;
    const wasNearBottom = !el || ((el.scrollHeight - el.scrollTop - el.clientHeight) < 80);
    setMessages(data);
    refreshUnread();
    if (wasNearBottom) {
      setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight), 50);
    }
  }, [refreshUnread]);

  useEffect(() => {
    api.get("/team-chat/muted-threads").then(r => setMutedThreads(new Set(r.data.muted_threads || [])));
    api.get("/organizations/me/chat-settings").then(r => setChatSettings(r.data));
    api.get("/team-chat/members").then(r => setMembers(r.data));
    api.get("/team-chat/blocked").then(r => setBlockedUsers(new Set(r.data.map(u => u.id))));
  }, []);

  useEffect(() => { loadThreads(); }, [loadThreads]);
  useEffect(() => {
    if (active) {
      // Clear stale messages the instant the selection changes so the
      // user never sees the previous conversation while the new one is
      // loading (fixes "chat selection shows stale conversation" bug).
      setMessages([]);
      loadMessages(active);
    }
  }, [active, loadMessages]);

  // Deep-link: react to ?thread=user:<id> | group:<id> & ?msg=<id>
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const t = q.get("thread");
    const m = q.get("msg");
    if (!t && !m) return;
    if (m) pendingTeamMsgIdRef.current = m;
    if (t && threads.length > 0) {
      let target = null;
      if (t.startsWith("user:")) {
        const uid = t.slice(5);
        target = threads.find(x => x.kind === "dm" && x.id === uid);
      } else if (t.startsWith("group:")) {
        const gid = t.slice(6);
        target = threads.find(x => x.kind === "group" && x.id === gid);
      }
      if (target && (!active || active.id !== target.id || active.kind !== target.kind)) {
        setActive(target);
      }
      // Strip params so a re-render or polling doesn't re-trigger
      navigate("/app/sms?tab=team", { replace: true });
    }
  }, [location.search, threads, active, navigate]);

  // Reset jump-to-latest counter when switching threads
  useEffect(() => {
    teamLastIdsRef.current = new Set();
    setTeamNewCount(0);
    setTeamShowJump(false);
  }, [active?.id, active?.kind]);

  // Track new incoming messages while user is scrolled up.
  // On first load for a thread (prevIds is empty), snap to bottom (unless we have a pending msg to flash).
  // If pendingTeamMsgIdRef is set, scroll the matching MessageBubble into view + flash.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const prevIds = teamLastIdsRef.current;
    const isInitial = prevIds.size === 0;
    const incomingNew = messages.filter(m => !prevIds.has(m.id) && m.from_user_id !== user?.id && !isInitial);
    const pending = pendingTeamMsgIdRef.current;
    const pendingPresent = pending && messages.some(m => m.id === pending);

    if (pendingPresent) {
      requestAnimationFrame(() => {
        const node = document.querySelector(`[data-testid="team-msg-${CSS.escape(pending)}"]`);
        if (node) {
          node.scrollIntoView({ behavior: "smooth", block: "center" });
          node.classList.add("msg-flash");
          setTimeout(() => node.classList.remove("msg-flash"), 2500);
        }
        pendingTeamMsgIdRef.current = null;
      });
    } else if (isInitial) {
      // Initial load for this thread — snap to bottom after paint
      requestAnimationFrame(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; });
    } else if (!isNearBottom(el) && incomingNew.length > 0) {
      setTeamNewCount(c => c + incomingNew.length);
    }
    teamLastIdsRef.current = new Set(messages.map(m => m.id));
  }, [messages, user?.id]);

  useEffect(() => {
    const id = setInterval(() => {
      loadThreads();
      if (active) loadMessages(active);
    }, 5000);
    return () => clearInterval(id);
  }, [active, loadThreads, loadMessages]);

  // Search debounce
  useEffect(() => {
    if (!search.trim()) { setSearchResults(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/team-chat/search?q=${encodeURIComponent(search.trim())}`);
        setSearchResults(data);
      } catch (e) { console.error(e); }
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  const activeThreadKey = active
    ? (active.kind === "group" ? `group:${active.id}` : [user?.id, active.id].sort().join(":"))
    : null;
  const isActiveMuted = activeThreadKey && mutedThreads.has(activeThreadKey);

  const send = async (e) => {
    e?.preventDefault();
    // iter160 — the composer is now Tiptap; pull the serialized body
    // directly from the editor so we always send the most recent state
    // (React state may still be one tick behind when Enter fires).
    const editorBody = editorRef.current?.getBody?.() ?? body;
    if ((!editorBody.trim() && pendingAttachments.length === 0) || !active) return;
    if ((editorRef.current?.getVisibleLength?.() ?? 0) > 4000) {
      alert("Message exceeds 4000 characters"); return;
    }
    const txt = editorBody.trim();
    const atts = pendingAttachments;
    const pri = priority;
    setBody(""); editorRef.current?.clear?.(); setVisibleCount(0);
    setReplyTo(null); setPendingAttachments([]); setPriority(null);
    try {
      const payload = active.kind === "group"
        ? { group_id: active.id, body: txt, attachments: atts, priority: pri }
        : { to_user_id: active.id, body: txt, attachments: atts, priority: pri };
      if (replyTo) payload.reply_to = replyTo.id;
      const { data } = await api.post("/team-chat/messages", payload);
      setMessages(m => [...m, data]);
      loadThreads();
      setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight), 50);
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.detail || "Send failed");
      setBody(txt); setPendingAttachments(atts); setPriority(pri);
    }
  };

  const onFilePicked = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/team-chat/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPendingAttachments(arr => [...arr, data]);
      }
    } catch (err) { alert(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ""; }
  };

  // Paste handler — handles THREE kinds of clipboard content:
  //
  //   1. Files/screenshots (Cmd+V of a screenshot, image, or attached
  //      file): iterate DataTransferItemList, upload to /team-chat/upload,
  //      stage as pending attachments. Same as before.
  //   2. Rich HTML text (Word / Google Docs / Outlook / Teams / Slack /
  //      web page): parse the clipboard's `text/html` blob through the
  //      shared `sanitizeHtmlToMessage` helper, which strips Office /
  //      Google `mso-*` markup, scripts, styles, tracking, colours, and
  //      classes; converts <p>/<br> to newlines; converts <ul>/<ol> to
  //      `- ` / `1. ` prefixed lines; converts <b>/<strong> to
  //      `**bold**`, <i>/<em> to `_italic_`, and <a> to
  //      `label (url)`. Result is a plain string that the existing
  //      text-only API + DB + renderer round-trip natively.
  //   3. Plain text with multiple lines: normalise line endings only —
  //      preserve blank lines from Shift+Enter/Shift+Enter round-trips.
  const onComposePaste = async (e) => {
    const dt = e.clipboardData;
    if (!dt) return;

    // ── (1) Files/screenshots ────────────────────────────────────────
    const filesToUpload = [];
    for (const item of dt.items || []) {
      if (item.kind === "file") {
        const f = item.getAsFile();
        if (f) filesToUpload.push(f);
      }
    }
    if (filesToUpload.length) {
      e.preventDefault();
      setUploading(true);
      try {
        for (const f of filesToUpload) {
          const cleanName = f.name && f.name !== "image.png"
            ? f.name
            : `screenshot-${new Date().toISOString().replace(/[:.]/g, "-").slice(0, -5)}.png`;
          const fd = new FormData();
          fd.append("file", new File([f], cleanName, { type: f.type }));
          const { data } = await api.post("/team-chat/upload", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          setPendingAttachments(arr => [...arr, data]);
        }
      } catch (err) {
        alert(err?.response?.data?.detail || "Paste upload failed");
      } finally {
        setUploading(false);
      }
      return;
    }

    // ── (2) Rich HTML paste ──────────────────────────────────────────
    const html = dt.getData("text/html");
    const plain = dt.getData("text/plain");
    let insert;
    if (html && html.trim()) {
      insert = sanitizeHtmlToMessage(html);
      // Fall back to plain text if sanitization somehow yielded nothing.
      if (!insert && plain) insert = sanitizePlainTextToMessage(plain);
    } else if (plain) {
      // ── (3) Plain-text paste ────────────────────────────────────────
      insert = sanitizePlainTextToMessage(plain);
    } else {
      return; // nothing usable — let the browser do whatever.
    }
    if (!insert) return;

    // Splice the sanitized text into the current textarea selection
    // instead of letting the browser paste the raw HTML.
    e.preventDefault();
    const el = inputRef.current;
    if (!el) { setBody(b => (b || "") + insert); return; }
    const start = el.selectionStart ?? body.length;
    const end   = el.selectionEnd   ?? body.length;
    const next  = (body || "").slice(0, start) + insert + (body || "").slice(end);
    setBody(next);
    // Restore caret to just after the inserted content on next tick.
    requestAnimationFrame(() => {
      const pos = start + insert.length;
      try { el.setSelectionRange(pos, pos); el.focus(); } catch { /* jsdom */ }
    });
  };

  // Enter/Shift+Enter handling. HTML <input> can't hold newlines at all,
  // which is why the composer became a <textarea> in this iteration.
  //   Enter (no modifiers) → submit the form (send message).
  //   Shift+Enter          → INSERT a newline character. Never sends.
  //   Cmd/Ctrl/Alt+Enter   → newline (Slack/Teams parity — keeps power
  //                          users from accidentally sending).
  // We never call preventDefault on Shift+Enter — the textarea's native
  // "insert \n at caret" behaviour is exactly what we want, so we just
  // let the event through.
  const onComposeKeyDown = (e) => {
    if (!shouldSendOnEnter(e)) return;   // Shift+Enter, Cmd+Enter, IME, other keys
    e.preventDefault();                   // suppress the default \n insertion
    send(e);
  };

  const removeAttachment = (path) => setPendingAttachments(arr => arr.filter(a => a.path !== path));

  const sendVoiceNote = async (att) => {
    if (!active) return;
    setRecordingVoice(false);
    try {
      const payload = active.kind === "group"
        ? { group_id: active.id, body: "", attachments: [att] }
        : { to_user_id: active.id, body: "", attachments: [att] };
      const { data } = await api.post("/team-chat/messages", payload);
      setMessages(m => [...m, data]);
      loadThreads();
      setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight), 50);
    } catch (err) { alert(err.response?.data?.detail || "Failed to send voice note"); }
  };

  const toggleBlock = async () => {
    if (!active || active.kind !== "dm") return;
    const isBlocked = blockedUsers.has(active.id);
    try {
      if (isBlocked) await api.delete(`/team-chat/block/${active.id}`);
      else await api.post(`/team-chat/block/${active.id}`);
      setBlockedUsers(s => { const ns = new Set(s); isBlocked ? ns.delete(active.id) : ns.add(active.id); return ns; });
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  const onGroupSaved = (g) => {
    setGroupModal(null);
    loadThreads().then(() => setActive({ kind: "group", id: g.id, group: g }));
  };

  const toggleMute = async () => {
    if (!activeThreadKey) return;
    try {
      const { data } = await api.post(`/team-chat/threads/${activeThreadKey}/mute`);
      setMutedThreads(s => { const ns = new Set(s); data.muted ? ns.add(activeThreadKey) : ns.delete(activeThreadKey); return ns; });
    } catch (e) { console.error(e); }
  };

  // Free internal P2P call — DM rings the other user; group rings every group member.
  const startInternalCall = async () => {
    if (!active) return;
    try {
      let participantIds = [];
      let title = "Internal call";
      if (active.kind === "dm") {
        participantIds = [active.id];
        title = `Call with ${active.user?.name || "teammate"}`;
      } else {
        participantIds = (active.group?.members || [])
          .map(m => m.id || m.user_id)
          .filter(id => id && id !== user?.id);
        title = `Group call: ${active.group?.name || "Team"}`;
      }
      if (participantIds.length === 0) {
        alert("No teammates to call in this conversation.");
        return;
      }
      const { data } = await api.post("/internal-call/start", { participant_ids: participantIds });
      // Post a "call card" to the chat thread so latecomers can join with one tap
      try {
        const cardPayload = {
          body: "",
          call_room_id: data.room_id,
          ...(active.kind === "group" ? { group_id: active.id } : { to_user_id: active.id }),
        };
        const card = await api.post("/team-chat/messages", cardPayload);
        if (card?.data) setMessages(prev => [...prev, card.data]);
      } catch (err) {
        console.warn("Could not post call-card to thread (non-fatal):", err?.message || err);
      }
      await joinRoom({ room_id: data.room_id, title, invitee_ids: data.invitee_ids || [] });
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || "Failed to start call");
    }
  };

  // @mention autocomplete: detect "@..." at cursor in input
  const onBodyChange = (e) => {
    const v = e.target.value;
    setBody(v);
    const cursor = e.target.selectionStart;
    const before = v.slice(0, cursor);
    const m = before.match(/(?:^|\s)@([a-zA-Z0-9._-]*)$/);
    if (m) { setMentionOpen(true); setMentionQuery(m[1].toLowerCase()); }
    else { setMentionOpen(false); }
  };

  // iter159 — auto-grow the composer textarea between 128px and 300px as
  // content changes (typing, pasting, mention insertion). Above 300px
  // the CSS `max-h-[300px]` + `overflow-y-auto` on the textarea let it
  // scroll internally, so the page itself never grows without bound.
  useEffect(() => {
    const el = inputRef.current;
    if (!el || el.tagName !== "TEXTAREA") return;
    // Reset to `auto` so scrollHeight reflects the CONTENT's natural height,
    // not the previous inline height (which would only ever grow).
    el.style.height = "auto";
    const min = 128;
    const max = 300;
    const next = Math.max(min, Math.min(max, el.scrollHeight));
    el.style.height = `${next}px`;
  }, [body]);

  const insertMention = (uname) => {
    if (!inputRef.current) return;
    const cursor = inputRef.current.selectionStart;
    const before = body.slice(0, cursor);
    const after = body.slice(cursor);
    const replaced = before.replace(/@([a-zA-Z0-9._-]*)$/, `@${uname} `);
    setBody(replaced + after);
    setMentionOpen(false);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const mentionCandidates = mentionOpen
    ? members.filter(m => m.username && m.username.toLowerCase().startsWith(mentionQuery)).slice(0, 6)
    : [];

  // Pinned messages strip
  const pinnedMessages = messages.filter(m => m.pinned && !m.deleted_for_all);

  return (
    <div className="hd-card hd-card--static grid grid-cols-12 h-full" data-testid="team-chat-panel">
      <div className="col-span-4 xl:col-span-3 border-r border-[var(--border)] overflow-y-auto min-h-0">
        <MessagesTabStrip tab={tab} smsUnread={smsUnread} teamUnread={teamUnread} onChange={onChangeTab} />
        <TeamThreadList
          threads={threads}
          active={active}
          setActive={setActive}
          search={search}
          setSearch={setSearch}
          searchResults={searchResults}
          mutedThreads={mutedThreads}
          currentUserId={user?.id}
          onCreateGroup={() => setGroupModal({ mode: "create" })}
        />
      </div>

      <div className="col-span-8 xl:col-span-9 flex flex-col min-h-0">
        {active ? (
          <>
            <TeamChatHeader
              active={active}
              isActiveMuted={isActiveMuted}
              blockedUsers={blockedUsers}
              onStartCall={startInternalCall}
              onToggleMute={toggleMute}
              onToggleBlock={toggleBlock}
              onEditGroup={() => setGroupModal({ mode: "edit", group: active.group })}
              activeTab={teamChatTab}
              onTabChange={setTeamChatTab}
            />

            {/* iter161-tabs — Chat / Files / Photos are wired to the
                `teamChatTab` state below. Chat renders the existing
                message list + composer; Files renders a list view of
                non-image attachments in this thread; Photos renders a
                grid view of image attachments. Files/Photos click into
                the existing AttachmentPreviewModal (iter157) via the
                shared AttachmentChip — no duplicate preview system,
                no auto-download, no re-upload. */}

            {pinnedMessages.length > 0 && teamChatTab === "chat" && (
              <div className="px-5 py-2 border-b border-[var(--border)] bg-[var(--bg-sidebar)] text-[11px] text-[var(--text-secondary)] flex items-center gap-2 shrink-0"
                   data-testid="pinned-strip">
                <span className="font-semibold uppercase tracking-widest text-[var(--accent)]">Pinned</span>
                <span className="truncate">{pinnedMessages[pinnedMessages.length-1].body}</span>
              </div>
            )}

            {teamChatTab === "files" && <FilesView messages={messages} />}
            {teamChatTab === "photos" && <PhotosView messages={messages} />}

            {teamChatTab === "chat" && (
            <>
            <div className="flex-1 min-h-0 relative">
              <div ref={scrollRef} onScroll={onTeamScroll}
                   className="absolute inset-0 overflow-y-auto py-3 px-2 bg-white"
                   data-testid="team-chat-thread">
                {messages.length === 0 && <div className="text-center text-[var(--text-muted)] py-10">No messages yet — start the conversation.</div>}
                {messages.map((m, i) => {
                  const prev = i > 0 ? messages[i-1] : null;
                  const sameDay = prev && new Date(prev.created_at).toDateString() === new Date(m.created_at).toDateString();
                  const sameSender = prev && prev.from_user_id === m.from_user_id;
                  const gapMins = prev ? (new Date(m.created_at) - new Date(prev.created_at)) / 60000 : Infinity;
                  const showHeader = !prev || !sameSender || gapMins > 5 || !sameDay;
                  const dateLabel = (!sameDay)
                    ? new Date(m.created_at).toLocaleDateString([], { day: 'numeric', month: 'long' }) + ' ' + new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    : null;
                  return (
                    <div key={m.id} data-testid={`team-msg-${m.id}`}>
                      {dateLabel && (
                        <div className="flex items-center gap-3 my-4 px-4" data-testid={`date-divider-${m.id}`}>
                          <div className="flex-1 border-t border-[var(--border)]" />
                          <span className="text-[11px] text-[var(--text-muted)] font-medium">{dateLabel}</span>
                          <div className="flex-1 border-t border-[var(--border)]" />
                        </div>
                      )}
                      <MessageBubble msg={m} mine={m.from_user_id === user?.id}
                                     isGroup={active.kind === "group"} currentUserId={user?.id}
                                     deleteWindowSec={chatSettings.delete_window_seconds}
                                     editWindowSec={chatSettings.edit_window_seconds}
                                     showHeader={showHeader}
                                     onChanged={() => { loadMessages(active); loadThreads(); }}
                                     onReply={(msg) => { setReplyTo(msg); inputRef.current?.focus(); }}
                                     onShowInfo={(msg) => setInfoMsgId(msg.id)}
                                     onForward={(msg) => setForwardMsg(msg)} />
                    </div>
                  );
                })}
              </div>
              {teamShowJump && (
                <button type="button" onClick={jumpToLatestTeam}
                        className="absolute bottom-4 right-4 z-10 bg-[#5B5FC7] text-white rounded-full min-w-9 h-9 px-2.5 flex items-center justify-center gap-1 shadow-lg hover:opacity-90 transition-opacity"
                        title="Jump to latest" data-testid="team-jump-to-latest">
                  <CaretDown size={16} weight="bold" />
                  {teamNewCount > 0 && (
                    <span className="text-[11px] font-bold leading-none" data-testid="team-jump-badge">
                      {teamNewCount > 99 ? "99+" : teamNewCount}
                    </span>
                  )}
                </button>
              )}
            </div>

            {replyTo && (
              <div className="border-t border-[var(--border)] px-4 py-2 bg-[var(--bg-sidebar)] flex items-center gap-2 shrink-0" data-testid="reply-preview">
                <ArrowBendUpLeft size={14} className="text-[var(--accent)]" />
                <div className="flex-1 min-w-0 text-[12px]">
                  <span className="font-semibold">{replyTo.from_user_name}</span>
                  <span className="text-[var(--text-secondary)] ml-2 truncate">{(replyTo.body || "").slice(0, 80)}</span>
                </div>
                <button onClick={() => setReplyTo(null)} className="p-1" data-testid="reply-preview-cancel"><X size={12} /></button>
              </div>
            )}

            {(pendingAttachments.length > 0 || uploading) && (
              <div className="border-t border-[var(--border)] px-3 py-2 bg-[var(--bg-sidebar)] flex flex-wrap items-center gap-2 shrink-0" data-testid="pending-attachments">
                {pendingAttachments.map(a => (
                  <div key={a.path} className="flex items-center gap-1.5 bg-white border border-[var(--border)] px-2 py-1 text-[12px]" data-testid={`pending-att-${a.path}`}>
                    <Paperclip size={12} />
                    <span className="truncate max-w-[140px]">{a.original_filename}</span>
                    <button onClick={() => removeAttachment(a.path)} className="ml-1 text-[var(--danger)]" data-testid={`pending-att-remove-${a.path}`}>
                      <X size={12} />
                    </button>
                  </div>
                ))}
                {uploading && <span className="text-[12px] text-[var(--text-muted)]" data-testid="upload-indicator">Uploading...</span>}
              </div>
            )}

            <form onSubmit={send} className="border-t border-[var(--border)] px-5 py-3 bg-white relative shrink-0">
              {recordingVoice && (
                <div className="mb-2">
                  <VoiceRecorder
                    onCancel={() => setRecordingVoice(false)}
                    onSent={sendVoiceNote}
                  />
                </div>
              )}
              {mentionOpen && mentionCandidates.length > 0 && (
                <div className="absolute bottom-full left-3 mb-1 bg-white border border-[var(--border)] shadow-lg min-w-[200px] z-30 rounded"
                     data-testid="mention-dropdown">
                  {mentionCandidates.map(m => (
                    <button type="button" key={m.id} onClick={() => insertMention(m.username)}
                            className="w-full text-left px-3 py-2 hover:bg-[var(--bg-sidebar)] text-[13px] flex flex-col"
                            data-testid={`mention-option-${m.username}`}>
                      <span className="font-semibold">{m.name}</span>
                      <span className="font-mono text-[11px] text-[var(--text-muted)]">@{m.username}</span>
                    </button>
                  ))}
                </div>
              )}
              <input ref={fileInputRef} type="file" multiple onChange={onFilePicked} className="hidden" data-testid="file-input" />
              <input ref={imageInputRef} type="file" multiple accept="image/*" onChange={onFilePicked} className="hidden" data-testid="image-input" />

              {/* iter161 — Single-row pill composer.
                  Icons + Send live on the same row as the Tiptap
                  editor. Container is `rounded-full` with an indigo
                  border, matching the requested reference. Editor
                  grows vertically inside the pill up to 300px (see
                  TeamChatEditor.jsx sizing rules); icons stay pinned
                  to the bottom-right so they remain reachable while
                  typing a long draft. Character counter moved to a
                  tiny helper below the pill (only visible once you
                  start typing) to keep the pill visually clean. */}
              <div className="relative flex items-end gap-1 border-t border-x border-gray-200 border-b-[3px] border-b-[#5B5FC7] bg-white rounded-t-3xl pl-3 pr-1 py-1 transition-colors" data-testid="team-chat-composer-pill">
                <div className="flex-1 min-w-0 self-stretch flex items-center">
                  <div className="flex-1 min-w-0">
                    <TeamChatEditor
                      ref={editorRef}
                      onSubmit={(mdBody) => {
                        setBody(mdBody);
                        requestAnimationFrame(() => {
                          const form = editorRef.current?.editor?.view?.dom?.closest("form");
                          if (form) form.requestSubmit?.();
                        });
                      }}
                      onChange={(ed) => {
                        setBody(ed.storage.characterCount ? ed.getText() : "");
                        setVisibleCount(ed.storage.characterCount?.characters?.() ?? 0);
                      }}
                    />
                  </div>
                </div>

                <div className="flex items-center gap-0 shrink-0 pb-0.5">
                  <div className="relative">
                    <button type="button" onClick={() => setEmojiPickerOpen((v) => !v)} className="p-1.5 hover:bg-[var(--bg-sidebar)] rounded-full text-[var(--text-secondary)]" title="Emoji"
                            data-testid="compose-emoji-btn">
                      <Smiley size={16} />
                    </button>
                    {emojiPickerOpen && (
                      <>
                        <div className="fixed inset-0 z-[59]" onClick={() => setEmojiPickerOpen(false)} />
                        <div className="absolute bottom-full right-0 mb-2 z-[60] bg-white border border-[var(--border)] shadow-2xl rounded-lg p-2 grid grid-cols-8 gap-0.5 w-[288px]"
                             data-testid="compose-emoji-picker">
                          {["😀","😂","😍","🙂","😉","😎","🥳","😢","😡","😴","🤔","🙄","😱","🤗","🤝","👋","👍","👎","👏","🙏","💯","✅","❌","⭐","🔥","💡","🎉","❤️","💔","💪","🚀","🎯","📞","📱","💬","📧","📌","🕐","📅","💰","🎁","☕","🍕","🌟","🌈","☀️","🌙","⚡"].map((e) => (
                            <button key={e} type="button" onClick={() => { editorRef.current?.insertText(e); setEmojiPickerOpen(false); }}
                                    className="text-[20px] hover:bg-[var(--bg-sidebar)] w-8 h-8 flex items-center justify-center rounded"
                                    data-testid={`compose-emoji-${e}`}>{e}</button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                  <button type="button" onClick={() => fileInputRef.current?.click()} className="p-1.5 hover:bg-[var(--bg-sidebar)] rounded-full text-[var(--text-secondary)]" title="Attach file"
                          data-testid="attach-btn" disabled={uploading}>
                    <Paperclip size={16} />
                  </button>
                  <button type="button" onClick={() => imageInputRef.current?.click()} className="p-1.5 hover:bg-[var(--bg-sidebar)] rounded-full text-[var(--text-secondary)]" title="Send image"
                          data-testid="compose-image-btn" disabled={uploading}>
                    <ImageSquare size={16} />
                  </button>
                  <button type="button" onClick={() => setRecordingVoice(true)} className="p-1.5 hover:bg-[var(--bg-sidebar)] rounded-full text-[var(--text-secondary)]" title="Record voice note"
                          data-testid="compose-mic-btn">
                    <Microphone size={16} />
                  </button>
                  <div className="relative">
                    <button type="button" onClick={() => setMoreOpen(v => !v)} className="p-1.5 hover:bg-[var(--bg-sidebar)] rounded-full text-[var(--text-secondary)]" title="More"
                            data-testid="compose-more-btn">
                      <Plus size={16} />
                    </button>
                    {moreOpen && (
                      <div className="absolute bottom-full right-0 mb-2 bg-white border border-[var(--border)] shadow-lg rounded p-2 min-w-[200px] z-30"
                           data-testid="compose-more-popover">
                        <div className="text-[10px] uppercase tracking-widest font-semibold text-[var(--text-muted)] mb-1">PRIORITY</div>
                        <div className="flex gap-1">
                          {["low","medium","high"].map(p => (
                            <button type="button" key={p} onClick={() => { setPriority(p === priority ? null : p); setMoreOpen(false); }}
                                    className={`flex-1 px-2 py-1 text-[11px] font-bold border rounded ${priority === p ? "bg-[#5B5FC7] text-white border-[#5B5FC7]" : "bg-white border-[var(--border)]"}`}
                                    data-testid={`compose-priority-${p}`}>
                              {p.toUpperCase()}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="w-px h-4 bg-[var(--border)] mx-0.5" />
                  <button type="submit" disabled={(visibleCount === 0 && pendingAttachments.length === 0) || visibleCount > 4000}
                          className="p-1.5 hover:bg-[var(--bg-sidebar)] rounded-full disabled:opacity-30 text-[#5B5FC7]"
                          data-testid="team-chat-send" title="Send">
                    <PaperPlaneTilt size={16} weight="fill" />
                  </button>
                </div>

                {priority && (
                  <div className="absolute -top-2 left-6 px-1.5 py-0.5 text-[9px] font-bold bg-[#5B5FC7] text-white rounded-sm" data-testid="compose-priority-indicator">
                    {priority.toUpperCase()}
                  </div>
                )}
              </div>
              {/* Char counter — subtle helper below the pill; only rendered
                  when the user has started typing so the empty state
                  stays visually clean. */}
              {visibleCount > 0 && (
                <div className="flex justify-end mt-1 px-2">
                  <span
                    className={`text-[10px] font-mono tabular-nums ${
                      visibleCount >= 4000 ? "text-[var(--danger)] font-semibold"
                      : visibleCount >= 3900 ? "text-orange-500"
                      : "text-[var(--text-muted)]"
                    }`}
                    data-testid="team-chat-char-counter"
                    aria-label={`${visibleCount} of 4000 characters used`}
                  >
                    {visibleCount}/4000
                  </span>
                </div>
              )}
            </form>
            </>
            )}
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-[var(--text-muted)]">
            <UsersThree size={48} />
            <div className="mt-3">Select a conversation</div>
          </div>
        )}
      </div>

      {groupModal && (
        <GroupModal mode={groupModal.mode} existing={groupModal.group}
                    onClose={() => setGroupModal(null)} onSaved={onGroupSaved}
                    onDeleted={() => { setGroupModal(null); setActive(null); loadThreads(); }}
                    currentUser={user} />
      )}
      {infoMsgId && <MessageInfoModal msgId={infoMsgId} onClose={() => setInfoMsgId(null)} />}
      {forwardMsg && <ForwardModal msg={forwardMsg} onClose={() => setForwardMsg(null)} onDone={() => { setForwardMsg(null); loadThreads(); }} />}
    </div>
  );
}

function GroupModal({ mode, existing, onClose, onSaved, onDeleted, currentUser }) {
  const [name, setName] = useState(existing?.name || "");
  const [memberIds, setMemberIds] = useState(new Set(existing?.member_ids || []));
  const [teammates, setTeammates] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/team-chat/members");
        setTeammates(data);
      } catch (e) { setErr(e.response?.data?.detail || e.message); }
    })();
  }, [currentUser?.id]);

  const toggle = (id) => setMemberIds(s => { const ns = new Set(s); ns.has(id) ? ns.delete(id) : ns.add(id); return ns; });

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setErr("Group name required"); return; }
    setBusy(true); setErr("");
    try {
      const payload = { name: name.trim(), member_ids: Array.from(memberIds) };
      const { data } = mode === "create"
        ? await api.post("/team-chat/groups", payload)
        : await api.patch(`/team-chat/groups/${existing.id}`, payload);
      onSaved?.(data);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!existing) return;
    if (!confirm(`Delete group "${existing.name}"? This removes all messages.`)) return;
    setBusy(true);
    try {
      await api.delete(`/team-chat/groups/${existing.id}`);
      onDeleted?.();
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const canDelete = mode === "edit" && existing && (existing.created_by === currentUser?.id || ["admin", "owner"].includes(currentUser?.role));

  return (
    <div className="hd-modal-scrim" data-testid="group-modal">
      <div className="hd-modal w-full max-w-lg flex flex-col max-h-[80vh]">
        <div className="px-5 py-4 border-b border-[var(--border)] flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-widest text-[var(--text-muted)] font-semibold">TEAM CHAT</div>
            <div className="font-display font-bold text-[18px]">{mode === "create" ? "Create group" : "Edit group"}</div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-[var(--bg-sidebar)]" data-testid="group-modal-close"><X size={16}/></button>
        </div>
        <form onSubmit={submit} className="flex-1 overflow-y-auto px-5 py-5">
          <label className="block text-[11px] tracking-widest font-semibold text-[var(--text-muted)] mb-1">GROUP NAME</label>
          <input autoFocus value={name} onChange={(e)=>setName(e.target.value)} placeholder="Sales team, Night shift, etc." className="input"
                 maxLength={80} data-testid="group-name-input" />

          <label className="block text-[11px] tracking-widest font-semibold text-[var(--text-muted)] mb-1 mt-4">MEMBERS ({memberIds.size} selected)</label>
          <div className="border border-[var(--border)] max-h-[260px] overflow-y-auto" data-testid="group-member-list">
            {teammates.length === 0 && <div className="px-4 py-6 text-center text-[12px] text-[var(--text-muted)]">No teammates yet — invite them from the Team page first.</div>}
            {teammates.map(u => {
              const checked = memberIds.has(u.id);
              return (
                <button type="button" key={u.id} onClick={() => toggle(u.id)}
                        data-testid={`group-member-${u.id}`}
                        className={`w-full flex items-center gap-3 px-3 py-2 border-b border-[var(--border)] hover:bg-[var(--bg-sidebar)] text-left ${checked ? "bg-[var(--bg-sidebar)]" : ""}`}>
                  <div className={`w-5 h-5 border-2 flex items-center justify-center ${checked ? "bg-[var(--accent)] border-[var(--accent)] text-white" : "border-[var(--border)]"}`}>
                    {checked && <Check size={12} weight="bold" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[13px] truncate">{u.name}</div>
                    <div className="text-[11px] text-[var(--text-muted)] truncate uppercase tracking-wider">{u.role}</div>
                  </div>
                </button>
              );
            })}
          </div>
          <div className="text-[11px] text-[var(--text-muted)] mt-1.5">You&apos;ll be added automatically.</div>

          {err && <div className="text-[12px] text-[var(--danger)] mt-3" data-testid="group-modal-error">{err}</div>}

          <div className="flex gap-2 mt-5">
            {canDelete && (
              <button type="button" onClick={remove} disabled={busy}
                      className="bg-white border border-[var(--danger)] text-[var(--danger)] hover:bg-red-50 py-2.5 px-3 flex items-center gap-1.5 font-semibold disabled:opacity-50"
                      data-testid="group-delete-btn">
                <Trash size={14} weight="bold" />
              </button>
            )}
            <button type="submit" disabled={busy || !name.trim()}
                    className="flex-1 bg-[var(--accent)] hover:opacity-90 disabled:opacity-50 text-white font-semibold py-2.5 flex items-center justify-center gap-2"
                    data-testid="group-save-btn">
              <Check size={14} weight="bold" /> {busy ? "Saving..." : (mode === "create" ? "Create group" : "Save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
